import { Array } from 'effect'

import { PRESET_MODELS } from '../../../../constant/presetModels.ts'
import type { ModelProperty } from '../../../../schemas/model/modelProperty.ts'
import type { NodePropertyName, ViewConfigKpi, ViewConfigTreeNode } from '../../../../schemas/view_config/index.ts'

// ユーザーと関連するユーザーグループを紐付けてノードを生成する
export function generateSalescoreUserNode(
  user: ViewConfigKpi['user'],
  modelProperty: ModelProperty | undefined,
  options?: {
    enableHistoricalGroupResolution?: boolean
    dateProperty?: { nodeName: string; modelName: string; propertyName: string }
  },
): ViewConfigTreeNode | undefined {
  if (user === undefined) {
    return undefined
  }

  const joinKey = getSalescoreUsersJoinKey(user.joinAs, user.property, modelProperty)
  const dateProperty = options?.enableHistoricalGroupResolution === true ? options.dateProperty : undefined

  return {
    type: `model`,
    name: PRESET_MODELS.USER_WITH_USER_GROUP.NAME,
    modelName: PRESET_MODELS.USER_WITH_USER_GROUP.NAME,
    referenceToProperty: {
      ...user.property,
      referenceTo: {
        modelName: PRESET_MODELS.USER_WITH_USER_GROUP.NAME,
        key: joinKey,
      },
      ...(dateProperty === undefined ? {} : { override: { onSql: 'TRUE' } }),
    },
    ...(dateProperty === undefined
      ? {}
      : {
          override: {
            tableSql: buildHistoricalGroupResolutionSql({
              parentNodeName: user.property.nodeName,
              parentJoinColumnName: user.property.propertyName,
              joinColumnName: joinKey,
              dateNodeName: dateProperty.nodeName,
              datePropertyName: dateProperty.propertyName,
            }),
            tableType: 'lateral_subquery',
            joinType: 'LEFT JOIN LATERAL',
          },
        }),
  }
}

function getSalescoreUsersJoinKey(
  joinAs: 'id' | 'email' | 'name',
  nodeProperty: NodePropertyName,
  modelProperty: ModelProperty | undefined,
) {
  if (joinAs !== 'id') {
    return joinAs
  }

  // referenceが複数ある場合はsalesforce_userをpickする (polymorphicを完全にケアできるものではないがsalesforceに関してはこれで問題ないはず)
  const referenceToModelName =
    // eslint-disable-next-line sonarjs/todo-tag
    modelProperty?.referenceTo?.find((x) => x.modelName === 'salesforce_user') ?? modelProperty?.referenceTo?.[0] // TODO: polymorphic relation
  if (modelProperty === undefined || referenceToModelName === undefined) {
    return `id`
  }

  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: 固有のkeyも考慮する必要がある
  switch (referenceToModelName.modelName) {
    case 'salesforce_user': {
      return `salesforce_user_id`
    }
    case 'salesforce_group': {
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: polymorphicなものがこのレイヤに影響している？旧Visualizeからの移行時のみの問題？
      return `salesforce_user_id`
    }
    case 'hubspot_owners': {
      return `hubspot_owner_id`
    }
    case 'hubspot_users': {
      return `hubspot_user_id`
    }
    // 2023/03 hubspotのengagementのロジックが間違っており、以下のモデル名がreferenceToで生成されていそうなので一旦追加
    case 'hubspot__owners': {
      return `hubspot_owner_id`
    }
    case 'hubspot__users': {
      return `hubspot_user_id`
    }
    case 'zoho_user': {
      return `zoho_user_id`
    }
    case 'next_sfa_user_info': {
      return `next_sfa_user_id`
    }
    case 'google_sheets_users': {
      return `google_sheets_user_id`
    }
    case 'comdesk_staff': {
      return `comdesk_user_id`
    }
    case 'senses_users': {
      return `senses_user_id`
    }
    case 'microsoft_dynamics_systemusers': {
      return `microsoft_dynamics_user_id`
    }
    case 'big_query_users': {
      return `big_query_user_id`
    }
    case 'kintone_user': {
      return `kintone_user_id`
    }
    default: {
      return `id`
    }
  }
}

function buildHistoricalGroupResolutionSql(argument: {
  parentNodeName: string
  parentJoinColumnName: string
  joinColumnName: string
  dateNodeName: string
  datePropertyName: string
}): string {
  const { parentNodeName, parentJoinColumnName, joinColumnName, dateNodeName, datePropertyName } = argument
  const dateCol = `"${dateNodeName}"."${datePropertyName}"`

  const dColumns = Array.flatMap(Array.range(1, 5), (i) => [
    `retained.user_group_d${i}_id`,
    `retained.user_group_d${i}_name`,
  ])

  return `SELECT
  ${dColumns.join(',\n  ')},
  retained.depth AS user_group_depth,
  retained.name AS user_group_name,
  resolved.*
FROM salescore_users AS resolved
LEFT JOIN salescore_user_group_assignment_histories AS history
  ON history.user_id = resolved.id
  AND (history.valid_from AT TIME ZONE 'UTC') <= ${dateCol}
  AND ((history.valid_to AT TIME ZONE 'UTC') > ${dateCol} OR history.valid_to IS NULL)
LEFT JOIN salescore_user_groups_retained AS retained
  ON retained.id = history.user_group_retained_id
WHERE resolved."${joinColumnName}" = "${parentNodeName}"."${parentJoinColumnName}"
ORDER BY history.valid_from DESC
LIMIT 1`
}
